import React, { useState, useEffect } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, Activity, Zap, Network } from 'lucide-react';
import { cpuColor } from '../topology';

// Ticks once a second while `enabled`, so live age labels update without a
// parent re-render.
function useNow(enabled) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [enabled]);
  return now;
}

// A RUNNING job whose newest solver output is older than this is spinning
// without forward progress (e.g. an MPI deadlock) — flag it.
const STALL_THRESHOLD_SEC = 300;

// Red "STALLED" pill shown when a running job stops writing output.
export function StallBadge({ lastActivityTs, status }) {
  const now = useNow(status === 'RUNNING' && !!lastActivityTs);
  if (status !== 'RUNNING' || !lastActivityTs) return null;
  const ageSec = now / 1000 - lastActivityTs;
  if (ageSec < STALL_THRESHOLD_SEC) return null;
  const mins = Math.floor(ageSec / 60);
  const label = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
  return (
    <span
      title={`No solver output written in ${label} — job may be stalled (processes spinning without progress).`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        backgroundColor: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.45)',
        color: '#f87171', fontSize: 11, fontWeight: 700, padding: '2px 8px',
        borderRadius: 999, whiteSpace: 'nowrap',
      }}
    >
      <AlertCircle style={{ width: 12, height: 12 }} />
      STALLED · no output {label}
    </span>
  );
}

// Amber pill showing the count of solver errors (from the .simerr file), e.g.
// PowerFLOW's "Velocity exceeds dynamic range" runtime errors.
export function WarningBadge({ count }) {
  const n = Number(count) || 0;
  if (n <= 0) return null;
  const label = n >= 1000 ? `${Math.round(n / 1000)}k` : String(n);
  return (
    <span
      title={`${n.toLocaleString()} solver errors written to the .simerr file (e.g. velocity-exceeds-dynamic-range). Check the case setup.`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        backgroundColor: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.45)',
        color: '#fbbf24', fontSize: 11, fontWeight: 700, padding: '2px 8px',
        borderRadius: 999, whiteSpace: 'nowrap',
      }}
    >
      <AlertTriangle style={{ width: 12, height: 12 }} />
      {label} errors
    </span>
  );
}

// Convergence assessment pill — green when the L/D ratio has settled (results
// trustworthy / run could stop early), amber while still converging.
export function FabricBadge({ fabric }) {
  if (!fabric || !fabric.provider) return null;
  const rdma = !!fabric.rdma;
  const c = rdma ? '#22c55e' : '#fbbf24';
  const bg = rdma ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.12)';
  return (
    <span
      title={rdma
        ? `MPI on RDMA/RoCE (${fabric.provider}) — ~2µs latency, near line-rate bandwidth. Fast multi-node scaling.`
        : `MPI on TCP (${fabric.provider}) — ~14µs latency, ~half bandwidth. Multi-node scaling is limited; consider the RDMA fabric.`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, backgroundColor: bg,
        border: `1px solid ${c}55`, color: c, fontSize: 11, fontWeight: 700,
        padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
      }}
    >
      {rdma ? <Zap style={{ width: 12, height: 12 }} /> : <Network style={{ width: 12, height: 12 }} />}
      {rdma ? `RDMA · ${fabric.provider}` : 'TCP'}
    </span>
  );
}

export function ConvergenceBadge({ status }) {
  if (!status || status.settled == null) return null;
  const settled = status.settled;
  const c = settled ? '#22c55e' : '#fbbf24';
  const bg = settled ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.12)';
  const label = settled
    ? `CONVERGED · L/D stable${status.settledAtTimestep ? ` since ts ${Number(status.settledAtTimestep).toLocaleString()}` : ''}`
    : `CONVERGING · L/D spread ${status.spreadPct}%`;
  return (
    <span
      title={settled
        ? 'Lift/drag ratio has settled within a 3% band — results are trustworthy and the run could be stopped to save compute.'
        : 'Lift/drag ratio still varies more than 3% over the trailing window — not yet settled.'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, backgroundColor: bg,
        border: `1px solid ${c}55`, color: c, fontSize: 11, fontWeight: 700,
        padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
      }}
    >
      {settled ? <CheckCircle style={{ width: 12, height: 12 }} /> : <Activity style={{ width: 12, height: 12 }} />}
      {label}
    </span>
  );
}

// Reusable dashboard card.
export function Card({ children, style = {} }) {
  return (
    <div style={{
      backgroundColor: '#0f172a',
      borderRadius: 20,
      padding: 24,
      border: '1px solid #334155',
      overflow: 'hidden',
      ...style,
    }}>
      {children}
    </div>
  );
}

// Slim horizontal progress bar.
export function MiniBar({ percent, color, height = 6 }) {
  return (
    <div style={{
      height, backgroundColor: '#1f2937',
      borderRadius: height / 2, overflow: 'hidden',
    }}>
      <div style={{
        height: '100%',
        width: `${Math.min(Math.max(percent, 0), 100)}%`,
        backgroundColor: color,
        borderRadius: height / 2,
        transition: 'width 0.5s ease-out',
      }} />
    </div>
  );
}

// Donut chart showing cluster CPU load.
export function DonutChart({ value, max, sublabel, size = 180 }) {
  const r = 70;
  const sw = 14;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(value / max, 1) * c);
  const color = cpuColor(value);
  return (
    <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#1f2937" strokeWidth={sw} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={color} strokeWidth={sw} fill="none"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'all 1s' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{
          fontSize: 11, color: '#6b7280',
          textTransform: 'uppercase', letterSpacing: 1,
        }}>CPU LOAD</span>
        <span style={{
          fontSize: 42, fontWeight: 'bold',
          color: value >= 90 ? '#ef4444' : '#fff',
        }}>{value.toFixed(0)}%</span>
        <span style={{ fontSize: 13, color: '#6b7280' }}>{sublabel}</span>
      </div>
    </div>
  );
}

// Shared helper: filter resources down to known compute nodes.
export function getNodeData(resources, allNodes) {
  return allNodes.map(n => resources.find(r => r.node === n)).filter(Boolean);
}
